/** Default: India */
export const DEFAULT_COUNTRY_CODE = '91';

/** Common codes — India first (default); longest dial codes first after for parsing */
export const COUNTRY_OPTIONS = [
  { value: '91', label: 'India (+91)' },
  { value: '971', label: 'UAE (+971)' },
  { value: '966', label: 'Saudi Arabia (+966)' },
  { value: '65', label: 'Singapore (+65)' },
  { value: '61', label: 'Australia (+61)' },
  { value: '49', label: 'Germany (+49)' },
  { value: '33', label: 'France (+33)' },
  { value: '44', label: 'United Kingdom (+44)' },
  { value: '86', label: 'China (+86)' },
  { value: '81', label: 'Japan (+81)' },
  { value: '7', label: 'Russia/KZ (+7)' },
  { value: '1', label: 'United States (+1)' },
];

/**
 * Combine country dial code + national digits (no +) for API (server normalizes).
 */
export function combinePhoneDigits(countryCode, nationalDigits) {
  const cc = String(countryCode || '').replace(/\D/g, '');
  const nat = String(nationalDigits || '').replace(/\D/g, '');
  if (!nat) return '';
  return `${cc}${nat}`;
}

/**
 * Split stored phone into country + national for the form (defaults India).
 */
export function parseStoredPhoneForInputs(stored, defaultCc = DEFAULT_COUNTRY_CODE) {
  const d = String(stored || '').replace(/\D/g, '');
  if (!d) return { countryCode: defaultCc, national: '' };

  const sorted = [...COUNTRY_OPTIONS].sort((a, b) => b.value.length - a.value.length);
  for (const opt of sorted) {
    if (d.startsWith(opt.value) && d.length > opt.value.length) {
      return { countryCode: opt.value, national: d.slice(opt.value.length) };
    }
  }

  if (d.length === 10) {
    return { countryCode: defaultCc, national: d };
  }

  return { countryCode: defaultCc, national: d };
}

/**
 * Max digits allowed in the national field (excluding country code).
 * Prevents typing/pasting overly long numbers; India = 10, etc.
 */
export function maxNationalDigitsForCountry(countryCode) {
  const code = String(countryCode || '');
  const map = {
    91: 10,
    1: 10,
    44: 10,
    86: 11,
    81: 11,
    65: 8,
    61: 9,
    971: 9,
    966: 9,
    49: 11,
    33: 9,
    7: 10
  };
  return map[code] ?? 12;
}

/**
 * Sanitize national-field input: strip duplicate country prefix if user typed full international number, then cap length.
 */
export function normalizeNationalInput(rawDigits, countryCode) {
  const cc = String(countryCode || '').replace(/\D/g, '');
  let v = String(rawDigits || '').replace(/\D/g, '');
  const maxNat = maxNationalDigitsForCountry(countryCode);
  if (cc && v.startsWith(cc) && v.length > maxNat) {
    v = v.slice(cc.length);
  }
  return v.slice(0, maxNat);
}

/**
 * Input placeholder that matches the selected country (not hardcoded to India).
 */
export function nationalPlaceholderForCountry(countryCode) {
  const code = String(countryCode || '');
  switch (code) {
    case '91':
      return '10-digit mobile (e.g. 9876543210)';
    case '1':
      return '10-digit number (area + number)';
    case '44':
      return 'UK number (without leading 0)';
    case '86':
      return '11-digit mobile number';
    case '81':
      return 'Mobile number (10–11 digits)';
    case '7':
      return '10-digit number (without +7)';
    case '971':
    case '966':
    case '65':
    case '61':
    case '49':
    case '33':
      return 'National number (no country code)';
    default:
      return 'National number (no country code)';
  }
}

/**
 * Short hint under the phone field, uses the selected country name.
 */
export function phoneFieldHintLine(countryCode) {
  const opt = COUNTRY_OPTIONS.find((o) => o.value === countryCode);
  const name = opt
    ? opt.label.replace(/\s*\([^)]+\)\s*$/, '').trim()
    : 'selected country';
  return `Enter the number for ${name} only—the country code is selected on the left.`;
}
